import React, { useEffect, useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, ChevronRight, Star, Sparkles } from 'lucide-react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import ProductCard from '../components/ProductCard';
import SEO from '../components/SEO';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Landing = () => {
  const [featuredProducts, setFeaturedProducts] = useState([]);
  const [promotions, setPromotions] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeService, setActiveService] = useState(0);

  /*
  const services = [
    { title: 'Business Cards', desc: 'Premium matte & foil finishes', image: 'https://images.unsplash.com/photo-1718670013988-c6e3edb92345?w=800' },
    { title: 'Executive Pens', desc: 'Crafted for distinction', image: 'https://images.unsplash.com/photo-1764087957302-ef0756ed8e0a?w=800' },
    { title: 'Leather Diaries', desc: 'Gold-edged elegance', image: 'https://images.unsplash.com/photo-1679891984409-0a3b1c4984ac?w=800' },
    { title: 'Corporate Apparel', desc: 'Wear your brand', image: 'https://images.unsplash.com/photo-1594938298603-c8148c4dae35?w=800' },
  ];*/

  const services = [
    { title: 'Corporate Gifts', desc: 'Leave a lasting impression', image: require('../assets/images/corporate_gifts.jpeg') },
    { title: 'Executive Pens', desc: 'Crafted for distinction', image: 'https://images.unsplash.com/photo-1764087957302-ef0756ed8e0a?w=800' },
    { title: 'Packaging', desc: 'Gold-edged elegance', image: require('../assets/images/branded_bags.jpeg') },
    { title: 'Corporate Apparel', desc: 'Wear your brand', image: require('../assets/images/suit_blue.jpeg') },
  ];

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [productsRes, promotionsRes, categoriesRes] = await Promise.all([
          axios.get(`${API}/products?featured=true&limit=4`),
          axios.get(`${API}/promotions`),
          axios.get(`${API}/categories`)
        ]);
        setFeaturedProducts(productsRes.data);
        setPromotions(promotionsRes.data);
        setCategories(categoriesRes.data);
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();

    // Auto-rotate services
    const interval = setInterval(() => {
      setActiveService((prev) => (prev + 1) % 4);
    }, 4000);
    return () => clearInterval(interval);
  }, []);

  return (
    <div className="min-h-screen bg-white" data-testid="landing-page">
      <SEO 
        title="Premium Corporate Branding Products"
        description="Elevate your corporate identity with premium branding products. From exquisite business cards to executive accessories, we craft sophisticated branding products that define excellence and prestige."
        url="/"
        breadcrumbs={[
          { name: 'Home', url: '/' }
        ]}
      />
      {/* Hero Section - Split Design */}
      <section className="relative min-h-screen flex" data-testid="hero-section">
        {/* Left Content */}
        <div className="w-full lg:w-1/2 flex flex-col justify-center px-6 md:px-12 lg:px-24 pt-32 pb-16 lg:py-0 relative z-10">
          <div className="max-w-xl">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#0A0A0A] text-white text-xs uppercase tracking-widest mb-8 lg:mt-20 animate-fade-in">
              <Sparkles className="w-3 h-3 text-[#C9A962]" />
              Premium Branding Solutions
            </div>
            
            <h1 className="font-serif text-5xl md:text-6xl lg:text-7xl font-semibold tracking-tight text-[#0A0A0A] leading-[1.1] animate-fade-in-up">
              Elevate Your
              <span className="block">Corporate</span>
              <span className="block text-[#C9A962]">Identity</span>
            </h1>
            
            <p className="mt-8 text-lg text-[#737373] leading-relaxed max-w-md animate-fade-in-up stagger-2">
              Branding Yard is where brands are planted, nurtured, and grown. We are a creative agency driven by strategy, fueled by innovation, and committed to building brands that stand out and stay relevant. We offer packaging ,corporate gifts and corporate wear.
            </p>
            
            <div className="flex flex-wrap gap-4 mt-10 animate-fade-in-up stagger-3">
              <Button 
                asChild
                className="bg-[#0A0A0A] text-white hover:bg-[#262626] rounded-none h-14 px-10 btn-lift text-sm uppercase tracking-wide group"
                data-testid="hero-shop-now"
              >
                <Link to="/store">
                  Explore Collection
                  <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </Link>
              </Button>
              <Button 
                asChild
                variant="outline"
                className="rounded-none h-14 px-10 border-[#C9A962] text-[#0A0A0A] hover:bg-[#C9A962] hover:text-white hover:border-[#C9A962] text-sm uppercase tracking-wide"
                data-testid="hero-view-catalogue"
              >
                <a href="https://www.facebook.com/share/1C8zWXjoSK/" target="_blank" rel="noopener noreferrer">
                  View Catalogue
                </a>
              </Button>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-3 gap-8 mt-16 pt-8 border-t border-[#E5E5E5] animate-fade-in-up stagger-4">
              <div>
                <p className="font-serif text-3xl font-semibold text-[#0A0A0A]">500<span className="text-[#C9A962]">+</span></p>
                <p className="text-xs uppercase tracking-wider text-[#737373] mt-1">Happy Clients</p>
              </div>
              <div>
                <p className="font-serif text-3xl font-semibold text-[#0A0A0A]">10K<span className="text-[#C9A962]">+</span></p>
                <p className="text-xs uppercase tracking-wider text-[#737373] mt-1">Products Delivered</p>
              </div>
              <div>
                <p className="font-serif text-3xl font-semibold text-[#0A0A0A]">5<span className="text-[#C9A962]">★</span></p>
                <p className="text-xs uppercase tracking-wider text-[#737373] mt-1">Client Rating</p>
              </div>
            </div>
          </div>
        </div>

        {/* Right Image Grid */}
        <div className="hidden lg:block w-1/2 relative">
          <div className="absolute inset-0 grid grid-cols-2 gap-4 p-8 pt-24">
            {/* Main Large Image */}
            <div className="col-span-2 row-span-1 relative overflow-hidden group h-[45%]">
              <img 
                src="https://images.unsplash.com/photo-1764087957302-ef0756ed8e0a?w=1200"
                alt="Luxury pen on notebook"
                className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent"></div>
              <div className="absolute bottom-6 left-6 text-white">
                <p className="text-xs uppercase tracking-widest text-[#C9A962]">Featured</p>
                <p className="font-serif text-xl mt-1">Executive Pen Collection</p>
              </div>
            </div>
            
            {/* Two smaller images */}
            <div className="relative overflow-hidden group">
              <img 
              src={require('../assets/images/bottle.jpeg')}
              alt="Corporate gifts"
              className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
              loading="lazy"
              />
              <div className="absolute inset-0 bg-black/20 group-hover:bg-black/10 transition-colors"></div>
            </div>
            <div className="relative overflow-hidden group">
              <img 
              src={require('../assets/images/laptop_bags.jpeg')}
              alt="Branded packaging"
              className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
              loading="lazy"
              />
              <div className="absolute inset-0 bg-black/20 group-hover:bg-black/10 transition-colors"></div>
            </div>
            </div>
            
            {/* Gold accent line */}
          <div className="absolute top-24 left-0 w-1 h-32 bg-[#C9A962]"></div>
        </div>

        {/* Mobile Hero Image */}
        <div className="lg:hidden absolute inset-0 z-0">
          <img 
            src="https://images.unsplash.com/photo-1764087957302-ef0756ed8e0a?w=1200"
            alt="Luxury branding"
            className="w-full h-full object-cover opacity-10"
          />
        </div>
      </section>

      {/* Scrolling Services Banner */}
      <section className="bg-[#0A0A0A] py-4 overflow-hidden">
        <div className="marquee-container">
          <div className="marquee-content flex gap-12 animate-marquee">
            {[...Array(3)].map((_, i) => (
              <React.Fragment key={i}>
                {['Letterheads', 'Corporate Apparel', 'Executive Pens', 'Leather Diaries', 'Gift Sets'].map((item) => (
                  <span key={`${i}-${item}`} className="flex items-center gap-4 text-white/60 text-sm uppercase tracking-widest whitespace-nowrap">
                    <Star className="w-3 h-3 text-[#C9A962]" fill="#C9A962" />
                    {item}
                  </span>
                ))}
              </React.Fragment>
            ))}
          </div>
        </div>
      </section>

      {/* Interactive Services Showcase */}
      <section className="py-24 md:py-32 bg-[#FAFAFA]" data-testid="services-showcase">
        <div className="max-w-screen-2xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            {/* Left - Image Stack */}
            <div className="relative h-[500px] lg:h-[600px]">
              {services.map((service, idx) => (
                <div
                  key={service.title}
                  className={`absolute inset-0 transition-all duration-700 ${
                    idx === activeService 
                      ? 'opacity-100 scale-100 z-10' 
                      : 'opacity-0 scale-95 z-0'
                  }`}
                >
                  <div className="relative w-full h-full">
                    <img 
                      src={service.image}
                      alt={service.title}
                      className="w-full h-full object-cover"
                      loading="lazy"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent"></div>
                    
                    {/* Gold frame accent */}
                    <div className="absolute top-8 left-8 w-24 h-24 border-l-2 border-t-2 border-[#C9A962]"></div>
                    <div className="absolute bottom-8 right-8 w-24 h-24 border-r-2 border-b-2 border-[#C9A962]"></div>
                  </div>
                </div>
              ))}
              
              {/* Navigation dots */}
              <div className="absolute bottom-16 left-8 z-20 flex gap-3">
                {services.map((_, idx) => (
                  <button
                    key={idx}
                    onClick={() => setActiveService(idx)}
                    className={`w-8 h-1 transition-all ${
                      idx === activeService ? 'bg-[#C9A962] w-12' : 'bg-white/50'
                    }`}
                  />
                ))}
              </div>
            </div>

            {/* Right - Content */}
            <div>
              <p className="text-sm uppercase tracking-widest text-[#C9A962] mb-4">What We Offer</p>
              <h2 className="font-serif text-4xl md:text-5xl font-medium tracking-tight text-[#0A0A0A] leading-tight">
                Premium Branding
                <span className="block">Products & Services</span>
              </h2>
              
              <div className="mt-12 space-y-0">
                {services.map((service, idx) => (
                  <button
                    key={service.title}
                    onClick={() => setActiveService(idx)}
                    className={`w-full text-left py-6 border-b border-[#E5E5E5] transition-all ${
                      idx === activeService ? 'pl-4 border-l-2 border-l-[#C9A962]' : 'pl-0'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <h3 className={`font-serif text-xl ${idx === activeService ? 'text-[#0A0A0A]' : 'text-[#737373]'}`}>
                          {service.title}
                        </h3>
                        <p className={`text-sm mt-1 transition-all ${idx === activeService ? 'text-[#737373] opacity-100' : 'opacity-0 h-0'}`}>
                          {service.desc}
                        </p>
                      </div>
                      <ChevronRight className={`w-5 h-5 transition-all ${idx === activeService ? 'text-[#C9A962] translate-x-1' : 'text-[#D4D4D4]'}`} />
                    </div>
                  </button>
                ))}
              </div>

              <Button 
                asChild
                className="mt-10 bg-[#0A0A0A] text-white hover:bg-[#262626] rounded-none h-14 px-10 btn-lift"
              >
                <Link to="/store">
                  View All Products
                  <ArrowRight className="ml-2 w-4 h-4" />
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Promotions Section */}
      {promotions.length > 0 && (
        <section className="py-24 md:py-32 bg-white" data-testid="promotions-section">
          <div className="max-w-screen-2xl mx-auto px-6 md:px-12 lg:px-24">
            <div className="flex flex-col md:flex-row justify-between items-start md:items-end mb-12">
              <div>
                <p className="text-sm uppercase tracking-widest text-[#C9A962] mb-2">Special Offers</p>
                <h2 className="font-serif text-4xl md:text-5xl font-medium tracking-tight text-[#0A0A0A]">
                  Current Promotions
                </h2>
              </div>
            </div>
            
            <div className="grid md:grid-cols-2 gap-8">
              {promotions.slice(0, 2).map((promo, idx) => (
                <Link 
                  key={promo.id}
                  to={promo.link_url || '/store'}
                  className="group relative overflow-hidden bg-[#0A0A0A]"
                  data-testid={`promo-${promo.id}`}
                >
                  <div className="aspect-[16/10] relative">
                    <img 
                      src={promo.image_url}
                      alt={promo.title}
                      className="w-full h-full object-cover opacity-60 group-hover:opacity-40 group-hover:scale-105 transition-all duration-700"
                      loading="lazy"
                    />
                    
                    {/* Gold corner accents */}
                    <div className="absolute top-4 left-4 w-12 h-12 border-l-2 border-t-2 border-[#C9A962] opacity-0 group-hover:opacity-100 transition-opacity"></div>
                    <div className="absolute bottom-4 right-4 w-12 h-12 border-r-2 border-b-2 border-[#C9A962] opacity-0 group-hover:opacity-100 transition-opacity"></div>
                    
                    <div className="absolute inset-0 p-8 flex flex-col justify-end">
                      <p className="text-[#C9A962] text-sm uppercase tracking-wider mb-2">{promo.subtitle}</p>
                      <h3 className="font-serif text-3xl md:text-4xl font-semibold text-white">{promo.title}</h3>
                      <p className="text-white/70 mt-3 max-w-sm">{promo.description}</p>
                      <span className="inline-flex items-center text-[#C9A962] mt-6 text-sm font-medium group-hover:gap-3 gap-2 transition-all">
                        Shop Now <ArrowRight className="w-4 h-4" />
                      </span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Categories Grid */}
      <section className="py-24 md:py-32 bg-[#0A0A0A]" data-testid="categories-section">
        <div className="max-w-screen-2xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="text-center mb-16">
            <p className="text-sm uppercase tracking-widest text-[#C9A962] mb-4">Browse By</p>
            <h2 className="font-serif text-4xl md:text-5xl font-medium tracking-tight text-white">
              Product Categories
            </h2>
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            {categories.map((category, idx) => (
              <Link
                key={category.id}
                to={`/store?category=${category.slug}`}
                className="group relative p-8 border border-white/10 hover:border-[#C9A962]/50 hover:bg-[#C9A962]/5 transition-all text-center overflow-hidden"
                data-testid={`category-${category.slug}`}
              >
                <div className="absolute top-0 left-0 w-full h-0.5 bg-[#C9A962] transform scale-x-0 group-hover:scale-x-100 transition-transform origin-left"></div>
                <h3 className="text-white font-medium text-sm relative z-10">{category.name}</h3>
                <ChevronRight className="w-4 h-4 text-[#C9A962] mx-auto mt-3 opacity-0 group-hover:opacity-100 transition-opacity" />
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Products */}
      <section className="py-24 md:py-32 bg-white" data-testid="featured-products">
        <div className="max-w-screen-2xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-end mb-12">
            <div>
              <p className="text-sm uppercase tracking-widest text-[#C9A962] mb-2">Handpicked</p>
              <h2 className="font-serif text-4xl md:text-5xl font-medium tracking-tight text-[#0A0A0A]">
                Featured Products
              </h2>
            </div>
            <Link 
              to="/store"
              className="mt-4 md:mt-0 inline-flex items-center text-sm uppercase tracking-wide font-medium text-[#0A0A0A] hover:text-[#C9A962] hover:gap-3 gap-2 transition-all"
              data-testid="view-all-products"
            >
              View All <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
          
          {loading ? (
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
              {[1, 2, 3, 4].map((i) => (
                <div key={i} className="animate-pulse">
                  <div className="aspect-[3/4] bg-[#F5F5F5]"></div>
                  <div className="mt-4 h-4 bg-[#F5F5F5] w-3/4"></div>
                  <div className="mt-2 h-3 bg-[#F5F5F5] w-1/4"></div>
                </div>
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
              {featuredProducts.map((product) => (
                <ProductCard key={product.id} product={product} />
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-24 md:py-32 bg-[#FAFAFA]">
        <div className="max-w-screen-2xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <p className="text-sm uppercase tracking-widest text-[#C9A962] mb-4">The Branding Yard Difference</p>
              <h2 className="font-serif text-4xl md:text-5xl font-medium tracking-tight text-[#0A0A0A] leading-tight">
                Why Corporates
                <span className="block">Trust Us</span>
              </h2>
              
              <div className="mt-12 space-y-8">
                {[
                  { num: '01', title: 'Premium Quality', desc: 'Only the finest materials - heavyweight cardstock, genuine leather, premium metals.' },
                  { num: '02', title: 'Custom Branding', desc: 'Embossing, foil stamping, and custom printing to make your brand unique.' },
                  { num: '03', title: 'Fast Turnaround', desc: 'We understand business deadlines. Quick production without compromising quality.' },
                ].map((item) => (
                  <div key={item.num} className="flex gap-6 group">
                    <span className="font-serif text-3xl text-[#C9A962] font-semibold">{item.num}</span>
                    <div>
                      <h3 className="font-serif text-xl font-semibold text-[#0A0A0A] group-hover:text-[#C9A962] transition-colors">{item.title}</h3>
                      <p className="text-[#737373] mt-2">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            
            <div className="relative">
              <div className="aspect-square bg-[#0A0A0A] overflow-hidden">
                <img 
                  src="https://images.unsplash.com/photo-1692521248559-c3434d0baa81?w=800"
                  alt="Premium stationery"
                  className="w-full h-full object-cover opacity-80"
                  loading="lazy"
                />
              </div>
              {/* Gold accent */}
              <div className="absolute -bottom-4 -right-4 w-32 h-32 border-2 border-[#C9A962]"></div>
              <div className="absolute -top-4 -left-4 w-24 h-24 bg-[#C9A962]"></div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 md:py-32 bg-[#0A0A0A] relative overflow-hidden" data-testid="cta-section">
        {/* Background pattern */}
        <div className="absolute inset-0 opacity-5">
          <div className="absolute top-0 left-1/4 w-96 h-96 border border-white rounded-full"></div>
          <div className="absolute bottom-0 right-1/4 w-64 h-64 border border-white rounded-full"></div>
        </div>
        
        <div className="max-w-screen-2xl mx-auto px-6 md:px-12 lg:px-24 text-center relative z-10">
          <div className="inline-flex items-center gap-2 px-4 py-2 border border-[#C9A962]/30 text-[#C9A962] text-xs uppercase tracking-widest mb-8">
            <Sparkles className="w-3 h-3" />
            Start Your Journey
          </div>
          
          <h2 className="font-serif text-4xl md:text-6xl font-semibold tracking-tight text-white max-w-3xl mx-auto leading-tight">
            Ready to Elevate
            <span className="block text-[#C9A962]">Your Brand?</span>
          </h2>
          <p className="mt-6 text-lg text-[#A3A3A3] max-w-xl mx-auto">
            Discover our complete collection of premium branding products and transform 
            your corporate identity today.
          </p>
          <div className="flex flex-wrap justify-center gap-4 mt-10">
            <Button 
              asChild
              className="bg-[#C9A962] text-[#0A0A0A] hover:bg-[#B89952] rounded-none h-14 px-12 btn-lift text-sm uppercase tracking-wide font-semibold"
              data-testid="cta-explore"
            >
              <Link to="/store">
                Explore Collection
                <ArrowRight className="ml-2 w-4 h-4" />
              </Link>
            </Button>
            <Button 
              asChild
              variant="outline"
              className="rounded-none h-14 px-12 border-white/30 text-white hover:bg-white hover:text-[#0A0A0A] text-sm uppercase tracking-wide"
            >
              <a href="https://www.facebook.com/share/1C8zWXjoSK/" target="_blank" rel="noopener noreferrer">
                View on Facebook
              </a>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Landing;
